/**
 * OCR progress — Single Source für Kanban + Werkstatt.
 *
 * Vorher: Kanban hatte hardcoded TARGET_S=360 + 99%-Cap (aus paddle_vl-Zeit),
 * Werkstatt rechnete live mit avg_runtime_s. Zwei Codepfade = Glass-Box-Bruch.
 * Ab jetzt rufen beide diese Funktion + den `useQueueAvgRuntime` Hook.
 */
import { useEffect, useState } from "react";

export interface OcrProgress {
  pct: number;
  overBudget: boolean;
  /** Cap-Wert für Bar-Visualisierung (max 100), während pct die Wahrheit zeigen darf. */
  barPct: number;
  elapsedS: number;
}

export function computeOcrProgress(elapsedMs: number, avgMs: number | null): OcrProgress {
  const elapsedS = Math.max(0, Math.floor(elapsedMs / 1000));
  if (!avgMs || avgMs <= 0) {
    // Fallback wenn noch keine completions in der Historie: ungefähre 6min-Skala.
    const fallbackMs = 360_000;
    const pct = (elapsedMs / fallbackMs) * 100;
    return { pct, overBudget: pct > 100, barPct: Math.min(100, pct), elapsedS };
  }
  const pct = (elapsedMs / avgMs) * 100;
  return { pct, overBudget: pct > 100, barPct: Math.min(100, pct), elapsedS };
}

/** Polls /api/queue/status für avg_runtime_s. Geteilt zwischen Kanban + Werkstatt. */
export function useQueueAvgRuntime(pollMs: number = 30_000): number | null {
  const [avg, setAvg] = useState<number | null>(null);
  useEffect(() => {
    let alive = true;
    const fetchOnce = () => {
      fetch("api/queue/status?queue=card.ocr", { credentials: "same-origin" })
        .then(r => r.ok ? r.json() : null)
        .then(j => { if (alive) setAvg(j?.summary?.avg_runtime_s ?? null); })
        .catch(() => {});
    };
    fetchOnce();
    const t = setInterval(fetchOnce, pollMs);
    return () => { alive = false; clearInterval(t); };
  }, [pollMs]);
  return avg;
}

export function formatElapsed(s: number): string {
  const m = Math.floor(s / 60);
  const rs = s % 60;
  return m > 0 ? `${m}:${String(rs).padStart(2, "0")}` : `${rs}s`;
}
